import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

const CompanyDataSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const skeletonColors = {
    page: theme.palette.mode === 'light' ? '#f6f8fb' : '#10141C',
    block: theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e',
    paper: theme.palette.mode === 'light' ? '#ffffff' : '#1a1f2a',
    shine: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)'
  };

  const skeletonStyles = `
    .company-skeleton-page {
      min-height: 100vh;
      background: ${skeletonColors.page};
      display: flex;
      flex-direction: column;
    }

    /* Header - matching PageHeader structure */
    .company-skeleton-header {
      background: ${skeletonColors.paper};
      border-bottom: 1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#2f363e'};
    }

    .company-skeleton-header-inner {
      max-width: 1152px;
      margin: 0 auto;
      padding: 16px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .company-skel-logo-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      align-items: center;
    }

    .company-skel-logo {
      width: 120px;
      height: 40px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .company-skel-header-title {
      flex: 1;
      display: flex;
      justify-content: center;
    }

    .company-skel-header-title-block {
      width: 280px;
      height: 28px;
      background: ${skeletonColors.block};
      border-radius: 8px;
    }

    .company-skel-close-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      justify-content: flex-end;
    }

    .company-skel-close {
      width: ${isMobile ? '40px' : '36px'};
      height: ${isMobile ? '40px' : '36px'};
      background: ${skeletonColors.block};
      border-radius: 50%;
    }

    .company-skeleton-content {
      flex: 1;
      display: flex;
      justify-content: center;
      padding: ${isMobile ? '24px 16px' : '75px 24px 24px'};
    }

    .company-skeleton-card {
      background: ${skeletonColors.paper};
      border-radius: 16px;
      width: 100%;
      max-width: 1150px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    /* Desktop: Two columns */
    .company-skel-two-columns {
      display: flex;
      min-height: 500px;
    }

    .company-skel-left-column {
      width: 345px;
      padding: 32px 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      border-right: 1px solid ${theme.palette.mode === 'light' ? '#e8e8e8' : '#2f363e'};
    }

    .company-skel-right-column {
      flex: 1;
      padding: 32px;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    /* Mobile layout */
    .company-skel-mobile {
      display: flex;
      flex-direction: column;
    }

    .company-skel-avatar-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: ${isMobile ? '24px 16px' : '0'};
    }

    .company-skel-avatar {
      width: ${isMobile ? '80px' : '100px'};
      height: ${isMobile ? '80px' : '100px'};
      background: ${skeletonColors.block};
      border-radius: 50%;
      margin-bottom: 16px;
    }

    .company-skel-name {
      width: 140px;
      height: 18px;
      background: ${skeletonColors.block};
      border-radius: 6px;
      margin-bottom: 12px;
    }

    .company-skel-email {
      width: 180px;
      height: 14px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .company-skel-divider {
      width: 100%;
      height: 1px;
      background: ${theme.palette.mode === 'light' ? '#e8e8e8' : '#2f363e'};
      margin: 24px 0;
    }

    .company-skel-menu-item {
      width: 100%;
      height: 44px;
      background: ${skeletonColors.block};
      border-radius: 8px;
      margin-bottom: 8px;
    }

    /* Tabs */
    .company-skel-tabs-horizontal {
      display: flex;
      gap: 8px;
      padding: 16px;
      border-bottom: 1px solid ${theme.palette.mode === 'light' ? '#e8e8e8' : '#2f363e'};
    }

    .company-skel-tab {
      flex: 1;
      height: 40px;
      background: ${skeletonColors.block};
      border-radius: 8px;
    }

    /* Form fields */
    .company-skel-section-title {
      width: 180px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .company-skel-form-field {
      width: 100%;
      height: 56px;
      background: ${skeletonColors.block};
      border-radius: 8px;
    }

    .company-skel-form-row {
      display: flex;
      gap: 16px;
    }

    .company-skel-form-row .company-skel-form-field {
      flex: 1;
    }

    .company-skel-form-row .company-skel-form-field.small {
      flex: 0 0 100px;
    }

    .company-skel-save-button {
      width: 200px;
      height: 42px;
      background: ${skeletonColors.block};
      border-radius: 8px;
      align-self: flex-end;
      margin-top: 16px;
    }

    /* Footer */
    .company-skeleton-footer {
      padding: 24px;
      display: flex;
      justify-content: center;
    }

    .company-skel-footer-logo {
      width: 100px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Shimmer animation */
    .shimmer {
      position: relative;
      overflow: hidden;
    }

    .shimmer::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-150%);
      background: linear-gradient(90deg, transparent 0%, ${skeletonColors.shine} 50%, transparent 100%);
      animation: companyShimmer 1.6s ease-in-out infinite;
    }

    @keyframes companyShimmer {
      0% { transform: translateX(-150%); }
      100% { transform: translateX(150%); }
    }
  `;

  return (
    <div className="company-skeleton-page">
      <style>{skeletonStyles}</style>
      
      {/* Header - estructura de PageHeader */}
      <div className="company-skeleton-header">
        <div className="company-skeleton-header-inner">
          <div className="company-skel-logo-container">
            <div className="company-skel-logo shimmer" />
          </div>
          {!isMobile && (
            <div className="company-skel-header-title">
              <div className="company-skel-header-title-block shimmer" />
            </div>
          )}
          <div className="company-skel-close-container">
            <div className="company-skel-close shimmer" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="company-skeleton-content">
        <div className="company-skeleton-card">
          {isMobile ? (
            /* Mobile Layout */
            <div className="company-skel-mobile">
              <div className="company-skel-avatar-section">
                <div className="company-skel-avatar shimmer" />
                <div className="company-skel-name shimmer" />
                <div className="company-skel-email shimmer" />
              </div>
              
              <div className="company-skel-tabs-horizontal">
                <div className="company-skel-tab shimmer" />
                <div className="company-skel-tab shimmer" />
                <div className="company-skel-tab shimmer" />
              </div>

              <div className="company-skel-right-column">
                <div className="company-skel-section-title shimmer" />
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-save-button shimmer" style={{ alignSelf: 'stretch', width: '100%' }} />
              </div>
            </div>
          ) : (
            /* Desktop Layout */
            <div className="company-skel-two-columns">
              <div className="company-skel-left-column">
                <div className="company-skel-avatar shimmer" />
                <div className="company-skel-name shimmer" />
                <div className="company-skel-email shimmer" />
                <div className="company-skel-divider" />
                <div className="company-skel-menu-item shimmer" />
                <div className="company-skel-menu-item shimmer" />
                <div className="company-skel-menu-item shimmer" />
              </div>
              
              <div className="company-skel-right-column">
                <div className="company-skel-section-title shimmer" />
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-form-row">
                  <div className="company-skel-form-field small shimmer" />
                  <div className="company-skel-form-field shimmer" />
                </div>
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-form-field shimmer" />
                <div className="company-skel-save-button shimmer" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="company-skeleton-footer">
        <div className="company-skel-footer-logo shimmer" />
      </div>
    </div>
  );
};

export default CompanyDataSkeleton;
